import React, { useState, useEffect } from "react"
import {X, Mail, Phone, Building2, Calendar, CheckCircle, Clock, AlertCircle, FileText, ChevronDown, ChevronUp, FolderOpen} from 'lucide-react'
import { lumi } from "../lib/lumi"
import { ContactRequestDetail } from './ContactRequestDetail'

interface ContactRequest {
  _id: string
  firstName: string
  lastName: string
  email: string
  phone: string
  company: string
  message?: string
  quizResponseId: string
  // Résultats du quiz intégrés
  quizScore?: number
  quizTotalScore?: number
  quizResultLevel?: string
  quizResultMessage?: string
  quizResultRecommendation?: string
  quizResultColor?: string
  quizAnswers?: number[]
  status: 'pending' | 'contacted' | 'scheduled' | 'completed'
  assigned_to?: string
  assigned_to_name?: string
  comments?: any[]
  actions?: any[]
  createdAt: string
  updatedAt: string
}

interface QuizResponse {
  _id: string
  answers: number[]
  totalScore: number
  percentage: number
  resultLevel: string
  createdAt: string
}

interface ContactRequestsListProps {
  onClose: () => void
  embedded?: boolean
}

export function ContactRequestsList({ onClose, embedded = false }: ContactRequestsListProps) {
  const [requests, setRequests] = useState<ContactRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [selectedRequest, setSelectedRequest] = useState<ContactRequest | null>(null)
  const [quizResponses, setQuizResponses] = useState<{[key: string]: QuizResponse}>({})
  const [expandedRequests, setExpandedRequests] = useState<Set<string>>(new Set())
  const [detailRequest, setDetailRequest] = useState<ContactRequest | null>(null)

  useEffect(() => {
    loadRequests()
  }, [])

  const loadRequests = async () => {
    setLoading(true)
    try {
      const filter = filterStatus !== 'all' ? { status: filterStatus } : {}
      const result = await lumi.entities.contact_requests.list({
        filter,
        sort: { createdAt: -1 }
      })
      setRequests(result.list)
      console.log('Demandes de contact chargées:', result.list)
      
      // Charger les réponses du quiz pour chaque demande
      const quizResponsesMap: {[key: string]: QuizResponse} = {}
      for (const request of result.list) {
        if (request.quizResponseId) {
          try {
            const quizResponse = await lumi.entities.quiz_responses.list({
              filter: { _id: request.quizResponseId },
              limit: 1
            })
            if (quizResponse.list && quizResponse.list.length > 0) {
              quizResponsesMap[request._id] = quizResponse.list[0]
            }
          } catch (error) {
            console.error('Erreur lors du chargement du quiz:', error)
          }
        }
      }
      setQuizResponses(quizResponsesMap)
    } catch (error) {
      console.error('Erreur lors du chargement des demandes:', error)
    } finally {
      setLoading(false)
    }
  }

  const updateStatus = async (requestId: string, newStatus: string) => {
    try {
      await lumi.entities.contact_requests.update(requestId, {
        status: newStatus,
        updatedAt: new Date().toISOString()
      })
      console.log('Statut mis à jour:', requestId, newStatus)
      await loadRequests()
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-amber-600" />
      case 'contacted':
        return <Mail className="w-4 h-4 text-blue-600" />
      case 'scheduled':
        return <Calendar className="w-4 h-4 text-indigo-600" />
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-emerald-600" />
      default:
        return <AlertCircle className="w-4 h-4 text-gray-600" />
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'En attente'
      case 'contacted':
        return 'Contacté'
      case 'scheduled':
        return 'RDV planifié'
      case 'completed':
        return 'Terminé'
      default:
        return status
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
  }

  const toggleExpanded = (requestId: string) => {
    const newExpanded = new Set(expandedRequests)
    if (newExpanded.has(requestId)) {
      newExpanded.delete(requestId)
    } else {
      newExpanded.add(requestId)
    }
    setExpandedRequests(newExpanded)
  }

  const questions = [
    "À quelle fréquence prenez-vous du recul pour analyser la stratégie globale de votre entreprise ?",
    "Comment décririez-vous votre processus de prise de décision stratégique ?",
    "Avez-vous des tensions ou conflits récurrents dans votre entreprise (associés, équipe, management) ?",
    "Suivez-vous régulièrement vos indicateurs de performance (financiers, opérationnels, RH) ?",
    "Ressentez-vous une surcharge ou un sentiment d'être seul(e) face aux décisions ?",
    "Votre entreprise a-t-elle une vision stratégique claire à 1-3 ans ?",
    "Comment gérez-vous l'équilibre entre opérationnel et stratégie ?",
    "Avez-vous des outils ou processus structurés pour piloter votre entreprise ?",
    "Faites-vous appel à des regards extérieurs pour challenger vos décisions ?",
    "Comment décririez-vous votre capacité actuelle à faire croître votre entreprise ?",
    "Comment décririez-vous votre équilibre vie professionnelle / vie personnelle actuel ?",
    "Ressentez-vous des signes d'épuisement professionnel (fatigue chronique, stress, démotivation) ?",
    "Arrivez-vous à déconnecter complètement de votre entreprise (vacances, week-ends, soirées) ?",
    "Votre activité professionnelle impacte-t-elle négativement vos relations personnelles (famille, amis, couple) ?",
    "Prenez-vous soin de votre santé physique et mentale malgré vos responsabilités professionnelles ?"
  ]

  const getScoreColor = (score: number) => {
    if (score === 5) return 'text-red-600 bg-red-100'
    if (score === 4) return 'text-orange-600 bg-orange-100'
    if (score === 3) return 'text-amber-600 bg-amber-100'
    if (score === 2) return 'text-yellow-600 bg-yellow-100'
    if (score === 1) return 'text-lime-600 bg-lime-100'
    return 'text-green-600 bg-green-100'
  }

  if (detailRequest) {
    return (
      <ContactRequestDetail
        request={detailRequest}
        onClose={() => setDetailRequest(null)}
        onUpdate={() => {
          loadRequests()
          // Recharger la demande détaillée
          const updated = requests.find(r => r._id === detailRequest._id)
          if (updated) setDetailRequest(updated)
        }}
      />
    )
  }

  if (embedded) {
    return (
      <>
        {/* Filters */}
        <div className="p-4 border-b bg-gray-50 flex gap-2 flex-wrap">
          <button
            onClick={() => {
              setFilterStatus('all')
              loadRequests()
            }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterStatus === 'all'
                ? 'bg-teal-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            Toutes ({requests.length})
          </button>
          {['pending', 'contacted', 'scheduled', 'completed'].map((status) => (
            <button
              key={status}
              onClick={() => {
                setFilterStatus(status)
                loadRequests()
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                filterStatus === status
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {getStatusIcon(status)}
              {getStatusLabel(status)}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-teal-600 border-t-transparent"></div>
              <p className="text-gray-600 mt-4">Chargement des demandes...</p>
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">Aucune demande de contact pour le moment</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {requests.map((request) => (
                <div
                  key={request._id}
                  className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-teal-300 transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {request.firstName} {request.lastName}
                      </h3>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Mail className="w-4 h-4 text-teal-600" />
                          <a href={`mailto:${request.email}`} className="hover:text-teal-600">
                            {request.email}
                          </a>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone className="w-4 h-4 text-teal-600" />
                          <a href={`tel:${request.phone}`} className="hover:text-teal-600">
                            {request.phone}
                          </a>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Building2 className="w-4 h-4 text-teal-600" />
                          <span>{request.company}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar className="w-4 h-4 text-teal-600" />
                          <span>{formatDate(request.createdAt)}</span>
                        </div>
                      </div>
                      {request.message && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-700">{request.message}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Quiz Response Details */}
                  {(request.quizScore !== undefined || quizResponses[request._id]) && (
                    <div className="mt-4 border-t pt-4">
                      {/* Résumé du résultat depuis le dossier */}
                      {request.quizScore !== undefined && (
                        <div className="mb-4 bg-gradient-to-r from-teal-50 to-cyan-50 border-2 border-teal-200 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                              <FileText className="w-5 h-5 text-teal-600" />
                              Résultat du Quiz (Dossier)
                            </h4>
                            <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                              request.quizResultColor === 'red' ? 'bg-red-100 text-red-700' :
                              request.quizResultColor === 'orange' ? 'bg-orange-100 text-orange-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              Score: {request.quizScore}/100
                            </span>
                          </div>
                          <div className="bg-white rounded-lg p-4 space-y-2">
                            <p className="font-semibold text-gray-900">{request.quizResultLevel}</p>
                            <p className="text-sm text-gray-700">{request.quizResultMessage}</p>
                            {request.quizResultRecommendation && (
                              <div className="mt-3 pt-3 border-t border-teal-200">
                                <p className="text-sm font-medium text-teal-900">
                                  💡 Recommandation : {request.quizResultRecommendation}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Détail des réponses */}
                      {quizResponses[request._id] && (
                        <>
                          <button
                            onClick={() => toggleExpanded(request._id)}
                            className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium mb-3 transition-colors"
                          >
                            {expandedRequests.has(request._id) ? (
                              <ChevronUp className="w-5 h-5" />
                            ) : (
                              <ChevronDown className="w-5 h-5" />
                            )}
                            <span>Voir le détail des réponses question par question</span>
                            <span className="ml-2 px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-sm font-semibold">
                              {quizResponses[request._id].answers.length} questions
                            </span>
                          </button>

                          {expandedRequests.has(request._id) && (
                            <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                              {quizResponses[request._id].answers.map((score, index) => (
                                <div key={index} className="bg-white p-3 rounded-lg border border-gray-200">
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-gray-900 mb-1">
                                        Q{index + 1}. {questions[index]}
                                      </p>
                                    </div>
                                    <div className={`px-3 py-1 rounded-full text-sm font-bold ${getScoreColor(score)}`}>
                                      {score}/5
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Statut:</span>
                        <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full">
                          {getStatusIcon(request.status)}
                          <span className="text-sm font-medium">{getStatusLabel(request.status)}</span>
                        </div>
                      </div>
                      {request.assigned_to_name && (
                        <div className="flex items-center gap-2 px-3 py-1 bg-teal-50 rounded-full border border-teal-200">
                          <span className="text-xs text-gray-600">👤</span>
                          <span className="text-sm font-medium text-teal-900">{request.assigned_to_name}</span>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => setDetailRequest(request)}
                      className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium flex items-center gap-2"
                    >
                      <FolderOpen className="w-4 h-4" />
                      Ouvrir le dossier
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Demandes de Contact</h2>
            <p className="text-teal-100 text-sm mt-1">Suivi des candidatures suite au quiz de qualification</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Filters */}
        <div className="p-4 border-b bg-gray-50 flex gap-2 flex-wrap">
          <button
            onClick={() => {
              setFilterStatus('all')
              loadRequests()
            }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterStatus === 'all'
                ? 'bg-teal-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            Toutes ({requests.length})
          </button>
          {['pending', 'contacted', 'scheduled', 'completed'].map((status) => (
            <button
              key={status}
              onClick={() => {
                setFilterStatus(status)
                loadRequests()
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                filterStatus === status
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {getStatusIcon(status)}
              {getStatusLabel(status)}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-teal-600 border-t-transparent"></div>
              <p className="text-gray-600 mt-4">Chargement des demandes...</p>
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">Aucune demande de contact pour le moment</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {requests.map((request) => (
                <div
                  key={request._id}
                  className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-teal-300 transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {request.firstName} {request.lastName}
                      </h3>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Mail className="w-4 h-4 text-teal-600" />
                          <a href={`mailto:${request.email}`} className="hover:text-teal-600">
                            {request.email}
                          </a>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone className="w-4 h-4 text-teal-600" />
                          <a href={`tel:${request.phone}`} className="hover:text-teal-600">
                            {request.phone}
                          </a>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Building2 className="w-4 h-4 text-teal-600" />
                          <span>{request.company}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar className="w-4 h-4 text-teal-600" />
                          <span>{formatDate(request.createdAt)}</span>
                        </div>
                      </div>
                      {request.message && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-700">{request.message}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Quiz Response Details */}
                  {(request.quizScore !== undefined || quizResponses[request._id]) && (
                    <div className="mt-4 border-t pt-4">
                      {/* Résumé du résultat depuis le dossier */}
                      {request.quizScore !== undefined && (
                        <div className="mb-4 bg-gradient-to-r from-teal-50 to-cyan-50 border-2 border-teal-200 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                              <FileText className="w-5 h-5 text-teal-600" />
                              Résultat du Quiz (Dossier)
                            </h4>
                            <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                              request.quizResultColor === 'red' ? 'bg-red-100 text-red-700' :
                              request.quizResultColor === 'orange' ? 'bg-orange-100 text-orange-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              Score: {request.quizScore}/100
                            </span>
                          </div>
                          <div className="bg-white rounded-lg p-4 space-y-2">
                            <p className="font-semibold text-gray-900">{request.quizResultLevel}</p>
                            <p className="text-sm text-gray-700">{request.quizResultMessage}</p>
                            {request.quizResultRecommendation && (
                              <div className="mt-3 pt-3 border-t border-teal-200">
                                <p className="text-sm font-medium text-teal-900">
                                  💡 Recommandation : {request.quizResultRecommendation}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Détail des réponses */}
                      {quizResponses[request._id] && (
                        <>
                          <button
                            onClick={() => toggleExpanded(request._id)}
                            className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium mb-3 transition-colors"
                          >
                            {expandedRequests.has(request._id) ? (
                              <ChevronUp className="w-5 h-5" />
                            ) : (
                              <ChevronDown className="w-5 h-5" />
                            )}
                            <span>Voir le détail des réponses question par question</span>
                            <span className="ml-2 px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-sm font-semibold">
                              {quizResponses[request._id].answers.length} questions
                            </span>
                          </button>

                          {expandedRequests.has(request._id) && (
                            <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                              {quizResponses[request._id].answers.map((score, index) => (
                                <div key={index} className="bg-white p-3 rounded-lg border border-gray-200">
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-gray-900 mb-1">
                                        Q{index + 1}. {questions[index]}
                                      </p>
                                    </div>
                                    <div className={`px-3 py-1 rounded-full text-sm font-bold ${getScoreColor(score)}`}>
                                      {score}/5
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Statut:</span>
                        <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full">
                          {getStatusIcon(request.status)}
                          <span className="text-sm font-medium">{getStatusLabel(request.status)}</span>
                        </div>
                      </div>
                      {request.assigned_to_name && (
                        <div className="flex items-center gap-2 px-3 py-1 bg-teal-50 rounded-full border border-teal-200">
                          <span className="text-xs text-gray-600">👤</span>
                          <span className="text-sm font-medium text-teal-900">{request.assigned_to_name}</span>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => setDetailRequest(request)}
                      className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium flex items-center gap-2"
                    >
                      <FolderOpen className="w-4 h-4" />
                      Ouvrir le dossier
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
